// Shared cleaner for rich-text description/bio HTML.
//
// Pasted content from Word / Google Docs / web pages carries inline
// fonts, sizes, colors, spans, and invisible characters, which made
// saved descriptions render differently depending on where the text
// was copied from. This strips everything except basic structure
// (paragraphs, line breaks, bold/italic/underline, lists, links) so
// every description inherits the app's own typography.
import DOMPurify from 'dompurify';

const ALLOWED_TAGS = ['p', 'div', 'br', 'b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'a'];
const ALLOWED_ATTR = ['href', 'target', 'rel'];

// Normalize characters that differ by paste origin: zero-width chars
// (Word/web artifacts), non-breaking spaces, and curly quotes.
export function normalizeText(str) {
  return String(str ?? '')
    .replace(/[​-‍﻿]/g, '') // zero-width chars
    .replace(/ /g, ' ') // non-breaking space
    .replace(/[‘’‚]/g, "'") // curly single quotes
    .replace(/[“”„]/g, '"'); // curly double quotes
}

export function cleanDescriptionHtml(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(String(html), 'text/html');

  // Google Docs wraps every paste in <b style="font-weight:normal">;
  // with style attributes stripped that would bold the whole text, so
  // unwrap those tags before sanitizing.
  doc.body.querySelectorAll('b').forEach((b) => {
    if (/font-weight\s*:\s*normal/i.test(b.getAttribute('style') || '')) {
      b.replaceWith(...b.childNodes);
    }
  });

  // Google Docs (and some sites) express bold/italic/underline as
  // styled spans rather than semantic tags. Convert them so the
  // formatting survives the tag allowlist.
  doc.body.querySelectorAll('span[style], font[style]').forEach((span) => {
    const style = span.getAttribute('style') || '';
    let node = span;
    const wrap = (tag) => {
      const el = doc.createElement(tag);
      node.replaceWith(el);
      el.appendChild(node);
      node = el;
    };
    if (/font-weight\s*:\s*(bold|[7-9]00)/i.test(style)) wrap('strong');
    if (/font-style\s*:\s*italic/i.test(style)) wrap('em');
    if (/text-decoration[^;]*:\s*[^;]*underline/i.test(style)) wrap('u');
  });

  // Headings paste in from web pages but aren't part of the app's
  // description typography — downgrade them to bold paragraphs.
  doc.body.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((h) => {
    const p = doc.createElement('p');
    const strong = doc.createElement('strong');
    strong.append(...h.childNodes);
    p.appendChild(strong);
    h.replaceWith(p);
  });

  const purified = DOMPurify.sanitize(doc.body.innerHTML, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
  });
  return normalizeText(purified).replace(/&nbsp;/gi, ' ');
}

// Plain-text → minimal HTML, for pastes that carry no HTML flavor.
export function textToHtml(text) {
  const escaped = normalizeText(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped.replace(/\r\n|\r|\n/g, '<br>');
}
